// Shapes the regulation rows of /api/public/coverage/{country} for the country regulatory page. Every value comes
// from the imported regulations list (columns Product, Address Requirement, Legal requirements, Service restrictions)
// and the number lists (activation times); nothing is written per country here.
import { NUMBER_GROUPS } from './numberGroups';

// Number kinds as the backend sends them -> the page's number type groups (UC numbers sit with 2-way).
const KIND_GROUP = { local: 'local', national: 'national', mobile: 'mobile', '2way': '2way', uc: '2way', tollfree: 'tollfree', uifn: 'uifn' };
const TYPE_ORDER = ['local', 'national', 'mobile', '2way', 'tollfree', 'uifn'];

export const typeLabel = (key) => (NUMBER_GROUPS.find((g) => g.key === key) || { label: key }).label;

const joinLabels = (labels) => (labels.length < 2 ? labels.join('')
  : `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`);

const groupsOf = (kinds) => TYPE_ORDER.filter((t) => (kinds || []).some((k) => KIND_GROUP[k] === t));

/** Number type groups on offer (from the number lists). */
export const offeredTypes = (data) => {
  const have = new Set((data?.offers || []).map((o) => KIND_GROUP[o.number_kind]).filter(Boolean));
  return TYPE_ORDER.filter((t) => have.has(t));
};

/**
 * One tab per number type, with the regulation rows that cover it. Types whose rows are exactly the same are
 * merged into one tab ("Local & 2-Way"), so the same requirements are not repeated.
 * [{ id, keys, label, rows: [row], offered: [type] }]
 */
export const regulationTabs = (data) => {
  const rules = data?.rules || [];
  const offered = offeredTypes(data);
  const tabs = [];
  TYPE_ORDER.forEach((t) => {
    const idx = rules.map((r, i) => (groupsOf(r.kinds).includes(t) ? i : -1)).filter((i) => i >= 0);
    if (!idx.length && !offered.includes(t)) return;
    const sig = idx.join(',');
    const same = sig && tabs.find((tab) => tab.sig === sig);
    if (same) same.keys.push(t);
    else tabs.push({ sig, keys: [t], rows: idx.map((i) => rules[i]) });
  });
  return tabs.map(({ sig, ...tab }) => ({
    ...tab, id: tab.keys.join('-'), label: joinLabels(tab.keys.map(typeLabel)),
    offered: tab.keys.filter((k) => offered.includes(k)),
  }));
};

/** The number types a single row covers, as a label ("Local, 2-Way & UC"). */
export const rowLabel = (row) => {
  const kinds = row.kinds || [];
  const labels = groupsOf(kinds).flatMap((t) => (t === '2way' && kinds.includes('uc')
    ? [...(kinds.includes('2way') ? ['2-Way'] : []), 'UC'] : [typeLabel(t)]));
  return joinLabels(labels) || row.product;
};

const textKey = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * What every row of the country says alike: restrictions and wording points present in all rows (two rows or
 * more). Shown once under "Applies to all number types" and left out of each tab.
 */
export const commonToAll = (rows) => {
  if (rows.length < 2) return { rules: [], details: [] };
  const keys = rows.map((r) => new Set([...(r.rules || []), ...(r.details || []).map((d) => d.text)].map(textKey)));
  const inAll = (text) => keys.every((k) => k.has(textKey(text)));
  return {
    rules: (rows[0].rules || []).filter(inAll),
    details: (rows[0].details || []).filter((d) => !(d.items || []).length && inAll(d.text)),
  };
};

export const withoutCommon = (row, common) => {
  const rules = new Set(common.rules.map(textKey));
  const details = new Set(common.details.map((d) => textKey(d.text)));
  return {
    ...row,
    rules: (row.rules || []).filter((x) => !rules.has(textKey(x))),
    details: (row.details || []).filter((d) => (d.items || []).length || !details.has(textKey(d.text))),
  };
};

const ADDRESS_SCOPE = {
  worldwide: { short: 'Anywhere', long: 'An address anywhere in the world' },
  country: { short: (c) => `In ${c}`, long: (c) => `An address in ${c}` },
  region: { short: 'In the number’s area', long: 'An address in the same area (area code) as the number' },
  region_some: { short: 'Anywhere (some cities: local)', long: 'Anywhere; some cities need an address in that city' },
  ask: { short: 'To be confirmed', long: 'Confirmed when you order' },
};

/** { short, long } address wording for a row's address_scope. */
export const addressScope = (scope, country) => {
  const s = ADDRESS_SCOPE[scope] || ADDRESS_SCOPE.ask;
  const fill = (v) => (typeof v === 'function' ? v(country) : v);
  return { short: fill(s.short), long: fill(s.long) };
};

/** Address summary across tabs: { short, detail }; detail names the types when they differ. */
export const addressSummary = (tabs, country) => {
  const scopes = [];
  tabs.forEach((tab) => tab.rows.forEach((r) => {
    const s = r.address_scope || 'ask';
    const hit = scopes.find((x) => x.scope === s);
    if (hit) { if (!hit.types.includes(tab.label)) hit.types.push(tab.label); } else scopes.push({ scope: s, types: [tab.label] });
  }));
  if (!scopes.length) return null;
  if (scopes.length === 1) {
    const a = addressScope(scopes[0].scope, country);
    return { short: a.short, detail: a.long };
  }
  return {
    short: 'Varies by type',
    detail: scopes.map((s) => ({ label: s.types.join(', '), value: addressScope(s.scope, country).short })),
  };
};

/** Distinct documents across rows, by document type (from the backend's document mapping). */
export const distinctDocuments = (rows) => {
  const seen = new Map();
  rows.forEach((r) => (r.documents || []).forEach((label, i) => {
    const key = r.document_types?.[i]?.key || label;
    if (!seen.has(key)) seen.set(key, label);
  }));
  return [...seen.values()];
};

/** Activation times by type group from the number lists: { short, detail } or null. */
export const activationSummary = (data, tabs) => {
  const byType = {};
  (data?.offers || []).forEach((o) => {
    const t = KIND_GROUP[o.number_kind];
    if (t && o.lead_time && !byType[t]) byType[t] = o.lead_time;
  });
  const values = [...new Set(Object.values(byType))];
  if (!values.length) return null;
  if (values.length === 1) return { short: values[0], detail: 'after your documents are approved', byType };
  return {
    short: 'Varies by type',
    detail: tabs.filter((t) => t.keys.some((k) => byType[k])).map((t) => ({ label: t.label, value: byType[t.keys.find((k) => byType[k])] })),
    byType,
  };
};

/** Splits text into plain strings and links, for wording that quotes the regulator's web pages. */
export const linkParts = (text) => {
  const parts = [];
  const re = /https?:\/\/[^\s)]+/g;
  let last = 0;
  let m;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push({ text: text.slice(last, m.index) });
    const url = m[0].replace(/[.,;]+$/, '');
    parts.push({ url });
    last = m.index + url.length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
};
